/** Two bands: what the campaign has achieved, and what the goal costs.
 *
 *  Asked for 2026-09-27 — "I need some more detailed review so they can
 *  understand quickly what they've to do to reach the goal, one is what
 *  they've achieved so far."
 *
 *  Deliberately two labelled bands rather than one row of figures. The
 *  same number means different things on each side: 533 invited is an
 *  achievement, 474 still to invite is a cost, and putting them in one
 *  undifferentiated grid makes the reader do that sorting themselves.
 */
import { useTranslation } from "react-i18next";

import type { Goal } from "./campaignGoal";

/** Fields are optional on purpose: this renders whatever the endpoint
 *  actually returned. A partial payload must degrade to zeroes, never
 *  throw — a crash here takes the entire campaign page with it, which
 *  is how this was found (E2E, 2026-09-27). */
type Breakdown = {
  total?: number;
  made?: number;
  never_invited?: number;
  conform?: number;
  non_conform?: number;
  sessions?: { closed: number; created: number };
};

export function CampaignProgressBands({
  breakdown,
  goal,
  capacityPerDay,
}: {
  breakdown: Breakdown;
  goal: Goal;
  capacityPerDay?: number;
}) {
  const { t } = useTranslation();
  const total = breakdown.total ?? 0;
  const made = breakdown.made ?? 0;
  const invited = Math.max(0, total - (breakdown.never_invited ?? 0));
  const judged = (breakdown.conform ?? 0) + (breakdown.non_conform ?? 0);
  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const spare =
    capacityPerDay !== undefined && Number.isFinite(goal.neededPerDay)
      ? Math.max(0, capacityPerDay - goal.neededPerDay)
      : null;

  return (
    <div className="flex flex-col gap-3">
      <Band
        tone="done"
        title={t("campaigns.goal.achievedTitle")}
        aside={t("campaigns.goal.activeDays", {
          count: goal.workingDaysElapsed,
        })}
      >
        <Metric
          label={t("campaigns.goal.inspected")}
          value={made}
          note={t("campaigns.goal.ofTotal", {
            pct: pct(made),
            total: total.toLocaleString(),
          })}
          accent="text-nb-mint"
        />
        <Metric
          label={t("campaigns.goal.closedSessions")}
          value={breakdown.sessions?.closed ?? 0}
          note={t("campaigns.goal.perDayNote", { n: goal.achievedPerDay })}
        />
        <Metric
          label={t("campaigns.goal.invited")}
          value={invited}
          note={t("campaigns.goal.ofTotal", {
            pct: pct(invited),
            total: total.toLocaleString(),
          })}
        />
        <Metric
          label={t("campaigns.goal.judged")}
          value={judged}
          note={t("campaigns.goal.judgedNote", {
            conform: breakdown.conform ?? 0,
            nonConform: breakdown.non_conform ?? 0,
          })}
        />
      </Band>

      {/* Nothing to require when it's finished, and nothing honest to
          require without a deadline. */}
      {goal.status !== "complete" && goal.status !== "no_deadline" && (
        <Band
          tone={goal.status === "off_pace" ? "todo" : "ok"}
          title={t("campaigns.goal.requiredTitle")}
          aside={t("campaigns.goal.byDeadline", {
            count: goal.workingDaysLeft,
          })}
        >
          <Metric
            label={t("campaigns.goal.remaining")}
            value={goal.remaining}
            note={t("campaigns.goal.ofTotal", {
              pct: pct(goal.remaining),
              total: total.toLocaleString(),
            })}
            accent={goal.status === "off_pace" ? "text-cvp-danger" : undefined}
          />
          <Metric
            label={t("campaigns.goal.perWorkingDay")}
            value={Number.isFinite(goal.neededPerDay) ? goal.neededPerDay : "—"}
            note={t("campaigns.goal.vsAchieved", { n: goal.achievedPerDay })}
            accent={goal.status === "off_pace" ? "text-cvp-danger" : undefined}
          />
          <Metric
            label={t("campaigns.goal.stillToInvite")}
            value={breakdown.never_invited ?? 0}
            note={t("campaigns.goal.waveNote")}
          />
          {spare !== null && (
            <Metric
              label={t("campaigns.goal.spareCapacity")}
              value={Math.round(spare)}
              note={t("campaigns.goal.spareNote")}
              accent="text-nb-mint"
            />
          )}
        </Band>
      )}
    </div>
  );
}

const TONES = {
  done: "border-nb-mint/28 bg-gradient-to-br from-nb-mint/8 to-nb-surface",
  todo: "border-cvp-danger/30 bg-gradient-to-br from-cvp-danger/10 to-nb-surface",
  ok: "border-nb-border bg-nb-surface",
} as const;

const TITLES = {
  done: "text-nb-mint",
  todo: "text-red-300",
  ok: "text-nb-text-2",
} as const;

function Band({
  tone,
  title,
  aside,
  children,
}: {
  tone: keyof typeof TONES;
  title: string;
  aside: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`rounded-xl border p-3.5 ${TONES[tone]}`}>
      <header className="mb-3 flex items-baseline gap-2">
        <h3
          className={`text-xs font-bold uppercase tracking-widest ${TITLES[tone]}`}
        >
          {title}
        </h3>
        <span className="ml-auto text-xs text-nb-text-3">{aside}</span>
      </header>
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">{children}</dl>
    </section>
  );
}

function Metric({
  label,
  value,
  note,
  accent,
}: {
  label: string;
  value: number | string;
  note: string;
  accent?: string;
}) {
  return (
    <div className="rounded-lg border border-nb-border bg-nb-bg/45 px-2.5 py-2">
      <dt className="text-[10px] uppercase tracking-wide text-nb-text-3">
        {label}
      </dt>
      <dd
        className={`mt-1 font-mono text-xl font-bold tabular-nums ${accent ?? ""}`}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </dd>
      <dd className="mt-0.5 text-[11px] text-nb-text-3">{note}</dd>
    </div>
  );
}
